import { get } from 'lodash';
import packageJson from '../../package.json';
import { ERROR_NAME } from '../data-types/error/errorEnums';
import { auth } from '../data-types/initialisation/initialisationConstants';
import { initialise, retryToConnect, socket } from '../initialisation/initialisation';
import { PATHS } from '../routing-system/appPaths';
import { ticketCreateCommand } from '../socket-types/ticketSocketTypes';
import { sessionRenew, sessionUnlock } from '../socket-types/userScoketTypes';
import { setPlacingBet, setSystemBetPlacingOrder } from '../state/actions/betActions';
import { setZimplerSignupDialogOpen } from '../state/actions/signupActions';
import { store } from '../state/store';
import { redirectToLogin } from '../utils/general';
import { performPostLogoutTasks } from '../utils/logout';
import { mustRenewToken, renewSession } from './userCommands';

/**
 * General function to send web socket commands to core-front
 */
export const sendCommand = async <T>(message: string, payload?: object): Promise<T> => {
  // initialise the socket if it is not connected
  if (!socket) {
    await initialise();
  } else if (!socket.connected) {
    await retryToConnect();
  }

  return new Promise((resolve, reject) => {
    if (socket && socket.connected) {
      const state = store.getState();
      const {
        navigation: { preventRedirectOnError },
        userData: { authentication },
        signup: { dialogs },
      } = state;

      if (authentication === auth.AUTHENTICATED && mustRenewToken() && message !== sessionRenew && message !== sessionUnlock) {
        try {
          renewSession();
        } catch {
          // do nothing
        }
      }

      return socket.emit(
        'command',
        {
          message,
          payload,
          timestamp: new Date().getTime(),
          userAgent: navigator.userAgent,
          webappVersion: get(window, 'app_version') || packageJson,
        },
        (response: T & { error: { name: string } }, error: Error) => {
          if (error) {
            throw error;
          }

          // TODO: handle unauthenticated response gracefully (add a nice message)
          if (response.error && response.error.name === ERROR_NAME.PRIVILEGE_ERROR) {
            if (!preventRedirectOnError) {
              if (authentication === auth.UNAUTHENTICATED) {
                if (window.location.pathname !== PATHS.LOGIN.ROOT) {
                  if (dialogs.zimplerSignupDialogOpen) {
                    setZimplerSignupDialogOpen(false);
                  }

                  if (message === ticketCreateCommand) {
                    setPlacingBet(false);
                    setSystemBetPlacingOrder([]);
                  }

                  redirectToLogin();
                }
              } else {
                performPostLogoutTasks(true);
              }
            }
            return reject(response);
          }

          return resolve(response);
        },
      );
    }
    return reject('Socket not initialised.');
  });
};
